// Copyright (c) 2026 Michael F. Kamprath
//
// numerals.js -- Numeral script rendering and per-locale defaults.
//
// Wraps src/data/numerals.json so callers don't need to know its
// internal structure.  Exports:
//
//   formatNumeral(value, script)
//     Look up the glyph string for `value` in `script`.  Throws on
//     unknown scripts and on out-of-range values (1..20 inclusive)
//     so callers don't silently render the wrong digit.
//
//   getDefaultScriptForLocale(locale)
//     Default numeral script for fresh users of the given locale.
//
//   getOptionsForLocale(locale)
//     The script options the settings picker should offer for the
//     given locale (always at least one).
//
//   getAvailableScripts()
//     All script keys defined in numerals.json (useful for tests
//     and for the settings UI string lookup).
//
// Static-imported JSON because numerals.json is small (<1 KB) and
// shipping it as a runtime fetch adds latency for every plaque
// render.  Vite inlines JSON imports as ES modules.

import data from './data/numerals.json';

const SCRIPTS = data.scripts ?? {};
const LOCALE_DEFAULTS = data.localeDefaults ?? {};
const LOCALE_OPTIONS = data.localeOptions ?? {};

// The numerals.json table covers 1..N where N is the array length
// (currently 20, matching the max-count slider).
const MIN_VALUE = 1;
const MAX_VALUE = (() => {
  // All script arrays are expected to have the same length; use the
  // shortest as a defensive cap so an out-of-sync entry can't cause
  // an undefined index lookup.
  let max = Infinity;
  for (const arr of Object.values(SCRIPTS)) {
    if (Array.isArray(arr)) max = Math.min(max, arr.length);
  }
  return Number.isFinite(max) ? max : 20;
})();

/**
 * Render an integer in the requested numeral script.
 *
 * @param {number} value   Integer in [1, MAX_VALUE].
 * @param {string} [script]  Script key from numerals.json (defaults to 'arabic').
 * @returns {string}
 * @throws {Error} on unknown script or out-of-range value.
 */
export function formatNumeral(value, script = 'arabic') {
  const arr = SCRIPTS[script];
  if (!Array.isArray(arr)) {
    throw new Error(`numerals: unknown script '${script}'`);
  }
  if (!Number.isInteger(value) || value < MIN_VALUE || value > MAX_VALUE) {
    throw new Error(
      `numerals: value ${value} out of range [${MIN_VALUE}, ${MAX_VALUE}]`,
    );
  }
  return arr[value - 1];
}

/**
 * Return the default numeral script for the given locale.  Falls back
 * to 'arabic' for unknown locales so tests and ad-hoc callers don't
 * have to handle null.
 *
 * @param {string} locale
 * @returns {string}
 */
export function getDefaultScriptForLocale(locale) {
  return LOCALE_DEFAULTS[locale] ?? 'arabic';
}

/**
 * Return the script options the settings picker should offer for the
 * given locale.  Always returns at least one option (defaults to the
 * locale's default script wrapped in an array, then 'arabic' as a
 * last-ditch fallback).
 *
 * @param {string} locale
 * @returns {string[]}
 */
export function getOptionsForLocale(locale) {
  const opts = LOCALE_OPTIONS[locale];
  if (Array.isArray(opts) && opts.length > 0) return opts.slice();
  const def = getDefaultScriptForLocale(locale);
  return [def];
}

/**
 * Return every script key declared in numerals.json.
 *
 * @returns {string[]}
 */
export function getAvailableScripts() {
  return Object.keys(SCRIPTS);
}

/**
 * Return a CSS font-family stack suitable for rendering numerals in
 * the given script.  Fredoka leads for arabic so the playful display
 * font is used for the home/learn UI; CJK scripts append the
 * appropriate Noto Sans CJK regional family because Fredoka has no
 * CJK coverage.  The browser's font matching engine walks the stack
 * and picks the first family that has the glyph.
 *
 * The Noto families are listed BEFORE generic fallbacks so the
 * webfont takes precedence over the OS's system CJK font (which can
 * have inconsistent stylistic treatment across macOS / Windows /
 * Linux / Android).  If the @font-face declarations have not loaded
 * yet, the browser falls back to the next family in the stack
 * (system CJK fonts) — never to Fredoka, which has no glyph.
 *
 * @param {string} script
 * @returns {string}
 */
export function getFontStackForScript(script) {
  switch (script) {
    case 'chinese':
      return '"Fredoka Variable", Fredoka, "Noto Sans SC", "PingFang SC", "Hiragino Sans GB", sans-serif';
    case 'japanese':
      return '"Fredoka Variable", Fredoka, "Noto Sans JP", "Hiragino Kaku Gothic ProN", "Yu Gothic", sans-serif';
    case 'sino-korean':
    case 'native-korean':
      return '"Fredoka Variable", Fredoka, "Noto Sans KR", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif';
    case 'arabic':
    default:
      return '"Fredoka Variable", Fredoka, sans-serif';
  }
}

/**
 * Trigger the browser to fetch the webfont for `script` if it isn't
 * already loaded.  Resolves once the font is ready (or immediately in
 * environments without document.fonts, e.g. Node/jsdom).
 *
 * Why this exists: PixiJS Text rasterises through Canvas2D at create
 * time.  If the @font-face woff2 hasn't been fetched yet, Pixi paints
 * the fallback family and the glyph never updates -- there's no
 * reflow trigger like a DOM node has.  Pre-warming via
 * document.fonts.load() against a sample of every glyph the script
 * actually renders forces the browser to fetch the woff2 BEFORE the
 * first Pixi text is built, so the right family wins on first paint.
 *
 * Sample glyphs come straight from numerals.json so an updated table
 * doesn't drift from the preload list.
 *
 * @param {string} script  Script key from numerals.json.
 * @param {number} [fontSize=64]  px size used for the load probe.
 *   Browsers cache by font-face not by size, so any positive size works.
 * @returns {Promise<void>}
 */
export async function preloadScriptFont(script, fontSize = 64) {
  if (typeof document === 'undefined' || !document.fonts) return;
  const arr = SCRIPTS[script];
  if (!Array.isArray(arr) || arr.length === 0) return;
  // Build one string containing every unique glyph the script renders.
  const sample = Array.from(new Set(arr.join(''))).join('');
  if (!sample) return;

  // We can't pass document.fonts.load a multi-family stack; it needs a
  // single resolved family per call.  Walk the stack so the right
  // webfont (Noto Sans SC / JP / KR) is the one that gets fetched.
  // System-fallback families in the stack will simply no-op at this
  // probe (they're already "loaded").
  const stack = getFontStackForScript(script);
  const families = stack.split(',').map((s) => s.trim()).filter(Boolean);
  await Promise.all(
    families.map((family) =>
      document.fonts.load(`700 ${fontSize}px ${family}`, sample).catch(() => null),
    ),
  );
}

/** @internal — exported for tests so they don't import the JSON path twice. */
export const __TEST__ = { SCRIPTS, LOCALE_DEFAULTS, LOCALE_OPTIONS, MAX_VALUE };
